// useGoogleAds.ts - Hook do zarządzania danymi Google Ads - BEZ AUTORYZACJI!
import { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '@/config/api';

interface Campaign {
  id: string;
  name: string;
  status: string;
  budget: number;
  cost: number;
  clicks: number;
  impressions: number;
  conversions: number;
  ctr: number;
  avgCpc: number;
  costPerConversion: number;
}

interface Keyword {
  id: string;
  keyword: string;
  text: string;
  campaignId: string;
  campaignName: string;
  matchType: string;
  status: string;
  clicks: number;
  impressions: number;
  ctr: number;
  avgCpc: number;
  conversions: number;
  conversionRate: number;
  cost: number;
  qualityScore: number;
  bid: number;
}

export const useGoogleAds = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pobierz kampanie z API - BEZ AUTORYZACJI!
  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Używamy endpointu który NIE wymaga autoryzacji
      const simpleEndpoint = 'https://api.plexisystem.pl/api/google-ads/campaigns-simple';
      console.log('🔗 Fetching campaigns from:', simpleEndpoint);
      
      const response = await fetch(simpleEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Otrzymano dane z API:', data);
        
        // Sprawdź strukturę odpowiedzi
        const campaignsData = data.campaigns || data;
        
        if (!Array.isArray(campaignsData)) {
          console.error('❌ Nieprawidłowa struktura danych:', data);
          setCampaigns([]);
          return;
        }
        
        // Mapuj dane z Google Ads API na nasz format
        const normalizedData = campaignsData.map((campaign: any) => ({
          id: campaign.id || campaign.resourceName || '',
          name: campaign.name || campaign.campaign_name || 'Bez nazwy',
          status: campaign.status || 'UNKNOWN',
          budget: Number(campaign.budgetDaily || campaign.budget || 0),
          cost: Number(campaign.cost || 0),
          clicks: Number(campaign.clicks || 0),
          impressions: Number(campaign.impressions || 0),
          conversions: Number(campaign.conversions || 0),
          ctr: Number(campaign.ctr || 0),
          avgCpc: Number(campaign.cpc || campaign.avgCpc || 0),
          costPerConversion: Number(campaign.costPerConversion || 0)
        }));
        
        console.log('📊 Znormalizowane kampanie:', normalizedData);
        setCampaigns(normalizedData);
        
        // Zapisz w localStorage na wszelki wypadek
        localStorage.setItem('googleAdsCampaigns', JSON.stringify(normalizedData));
        
        // Zapisz w window dla innych komponentów
        (window as any).__googleAdsCampaigns = normalizedData;
        
        // Wyślij event że dane są załadowane
        window.dispatchEvent(new CustomEvent('googleAdsDataLoaded', { 
          detail: { campaigns: normalizedData }
        }));
        
      } else {
        console.error('❌ API response not ok:', response.status, response.statusText);
        
        // Spróbuj pobrać dane z cache
        const cachedData = localStorage.getItem('googleAdsCampaigns');
        if (cachedData) {
          const cached = JSON.parse(cachedData);
          console.log('💾 Using cached campaigns:', cached);
          setCampaigns(cached);
        } else {
          setCampaigns([]);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      setError('Błąd połączenia z API');
      
      // Spróbuj użyć danych z cache
      const cachedData = localStorage.getItem('googleAdsCampaigns');
      if (cachedData) {
        const cached = JSON.parse(cachedData);
        console.log('💾 Using cached campaigns after error:', cached);
        setCampaigns(cached);
      } else {
        setCampaigns([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Pobierz słowa kluczowe - też bez autoryzacji
  const fetchKeywords = async (campaignId: string) => {
    try {
      const response = await fetch(`https://api.plexisystem.pl/api/google-ads/campaigns/${campaignId}/keywords`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Otrzymano słowa kluczowe:', data);
        const keywordsData = data.keywords || data || [];
        setKeywords(keywordsData);
        return keywordsData;
      } else {
        console.warn('⚠️ Nie udało się pobrać słów kluczowych');
        return [];
      }
    } catch (error) {
      console.error('Error fetching keywords:', error);
      return [];
    }
  };

  // Funkcje wymagające autoryzacji - zostawiamy jako stub
  const addKeyword = async (campaignId: string, keyword: string, matchType: string, bid: number) => {
    console.warn('⚠️ Dodawanie słów kluczowych wymaga pełnej autoryzacji Google Ads');
    throw new Error('Funkcja wymaga autoryzacji Google Ads');
  };

  const updateKeywordStatus = async (keywordId: string, status: string) => {
    console.warn('⚠️ Aktualizacja statusu wymaga pełnej autoryzacji Google Ads');
    throw new Error('Funkcja wymaga autoryzacji Google Ads');
  };

  const updateKeywordBid = async (keywordId: string, bid: number) => {
    console.warn('⚠️ Aktualizacja stawki wymaga pełnej autoryzacji Google Ads');
    throw new Error('Funkcja wymaga autoryzacji Google Ads');
  };

  const fetchKeywordsStats = async () => {
    try {
      const response = await fetch('https://api.plexisystem.pl/api/google-ads/keywords-stats', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        return await response.json();
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching keywords stats:', error);
      return null;
    }
  };

  useEffect(() => {
    // Pobierz kampanie przy montowaniu
    fetchCampaigns();
    
    // Odświeżaj co 5 minut
    const interval = setInterval(() => {
      console.log('🔄 Auto-refresh kampanii...');
      fetchCampaigns();
    }, 5 * 60 * 1000);
    
    // Nasłuchuj na eventy odświeżenia
    const handleRefresh = () => {
      console.log('🔄 Manual refresh requested');
      fetchCampaigns();
    };
    
    window.addEventListener('googleAds:refresh', handleRefresh);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('googleAds:refresh', handleRefresh);
    };
  }, []);

  return {
    campaigns,
    keywords,
    loading,
    error,
    refreshCampaigns: fetchCampaigns,
    fetchKeywords,
    addKeyword,
    updateKeywordStatus,
    updateKeywordBid,
    fetchKeywordsStats
  };
};
